/**
 * Sorts an array based on the order defined in a reference array.
 * Elements that appear in the reference order will be sorted according to their position
 * in the reference array. Elements not found in the reference order will appear at the
 * end of the sorted array, maintaining their relative order from the original array.
 *
 * **IMPORTANT**: This function modifies the original `arrayToSort` array in place.
 * If you need to preserve the original array, use the spread operator:
 * `sortBasedOnReferenceOrder([...arrayToSort], referenceOrder)`
 *
 * @template T The type of elements in the arrays
 * @param {T[]} arrayToSort - The array to be sorted (will be modified in place)
 * @param {T[]} referenceOrder - The reference array that defines the desired sort order
 * @returns {T[]} The sorted array (same reference as input arrayToSort)
 *
 * @example
 * // Basic usage
 * const fruits = ['banana', 'apple', 'cherry'];
 * const order = ['apple', 'banana', 'cherry'];
 * sortBasedOnReferenceOrder(fruits, order);
 * // fruits is now ['apple', 'banana', 'cherry']
 *
 * @example
 * // Elements not in reference appear at end
 * const items = ['d', 'b', 'a', 'e'];
 * const order = ['a', 'b', 'c'];
 * sortBasedOnReferenceOrder(items, order);
 * // items is now ['a', 'b', 'd', 'e']
 *
 * @example
 * // Preserving original array with spread operator
 * const original = ['c', 'a', 'b'];
 * const sorted = sortBasedOnReferenceOrder([...original], ['a', 'b', 'c']);
 * // original remains ['c', 'a', 'b']
 * // sorted is ['a', 'b', 'c']
 */
export function sortBasedOnReferenceOrder<T>(
  arrayToSort: T[],
  referenceOrder: T[]
) {
  return arrayToSort.sort((a, b) => {
    const orderA = referenceOrder.indexOf(a);
    const orderB = referenceOrder.indexOf(b);
    if (orderA === -1 && orderB === -1) {
      return 0;
    }
    if (orderA === -1) {
      return 1;
    }
    if (orderB === -1) {
      return -1;
    }
    return orderA - orderB;
  });
}
